/**
 * Account Indicator Component
 *
 * TASK-086: Account Selection Flow
 *
 * Displays authenticated user information in the navigation bar:
 * - User email and account name
 * - Demo account badge
 * - Logout button
 *
 * @module components/AccountIndicator
 */

import { useAccount } from '../contexts/AccountContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AccountIndicator Component
 *
 * Shows current logged-in user info and provides logout functionality.
 * Returns null if user is not authenticated.
 */
export function AccountIndicator() {
  const { account, user, clearAccount, isAuthenticated } = useAccount();
  const navigate = useNavigate();

  const handleLogout = () => {
    console.log('🚪 Logout clicked - clearing account and token');

    // Clear JWT token and account state
    clearAccount();

    // Also clear any other auth-related items
    localStorage.removeItem('auth_token');
    localStorage.removeItem('account');

    console.log('✅ Token cleared, navigating to login');

    // Show success notification
    toast.success('Logged out successfully');

    // Redirect to login page
    navigate('/login');
  };

  // Don't render if not authenticated
  if (!isAuthenticated || !account) return null;

  return (
    <div className="flex items-center gap-3 px-4 py-2 border-l border-slate-200">
      {/* Demo Account Badge */}
      {account.isDemo && (
        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
          DEMO
        </span>
      )}

      {/* User Information */}
      <div className="flex items-center gap-2">
        <User size={16} className="text-slate-500" />
        <span className="text-sm font-medium text-slate-700">
          {user?.firstName || user?.lastName
            ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
            : account.name}
        </span>
      </div>

      {/* Logout Button */}
      <button
        onClick={handleLogout}
        className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        title="Logout"
        aria-label="Logout"
      >
        <LogOut size={18} />
      </button>
    </div>
  );
}

export default AccountIndicator;
